import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { CheckIcon, Trash01Icon, Send01Icon } from '@untitledui-icons/react/line'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/base/buttons'
import { FormInput, FormSelect, FormSwitch, PhoneNumberInput } from '@/components/forms'
import { AlertDialog } from '@/components/ui/AlertDialog'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import {
  useUser,
  useUpdateUser,
  useDeleteUser,
  useLocales,
  getReadableLocaleName,
  useResendInvite,
} from '@/hooks/api/useUserManagement'

// Validation schema
const userEditSchema = z
  .object({
    first_name: z.string().min(1, 'app.validation.first_name_required'),
    last_name: z.string().min(1, 'app.validation.last_name_required'),
    email: z.string().email('app.validation.invalid_email'),
    phone_number: z.string().optional(),
    new_password: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val || val === '') return true
          return val.length >= 8
        },
        { message: 'app.validation.minLength' }
      )
      .refine(
        (val) => {
          if (!val || val === '') return true
          return /[A-Z]/.test(val)
        },
        { message: 'app.validation.password.uppercase' }
      )
      .refine(
        (val) => {
          if (!val || val === '') return true
          return /[!@#$%^&*]/.test(val)
        },
        { message: 'app.validation.password.special' }
      ),
    locale: z.string().min(1, 'app.validation.language_required'),
    two_factor_enabled: z.boolean(),
  })
  .refine(
    (data) => {
      if (data.two_factor_enabled && (!data.phone_number || data.phone_number.trim() === '')) {
        return false
      }
      return true
    },
    {
      message: 'app.validation.phone_required_2fa',
      path: ['phone_number'],
    }
  )

type UserEditFormValues = z.infer<typeof userEditSchema>

export function UserEditPage() {
  const { t } = useTranslation()
  const { id, customerId, partnerId } = useParams<{
    id: string
    customerId?: string
    partnerId?: string
  }>()
  const navigate = useNavigate()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  // Build base path for navigation
  const basePath = customerId
    ? `/customers/${customerId}/users`
    : partnerId
      ? `/partners/${partnerId}/users`
      : '/users'

  const userId = parseInt(id || '0', 10)

  // Fetch user data
  const { data: user, isLoading: userLoading } = useUser(userId)

  // Fetch locales
  const { data: locales = [] } = useLocales()

  // Mutations
  const updateUser = useUpdateUser()
  const deleteUser = useDeleteUser()
  const resendInvite = useResendInvite()
  
  // Check if we can send invite (any non-active user)
  const canSendInvite = user && !user.is_active
  const isPendingStatus = user?.status === 'pending' || (!user?.status && !user?.is_active)

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<UserEditFormValues>({
    resolver: zodResolver(userEditSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      new_password: '',
      locale: 'en',
      two_factor_enabled: false,
    },
  })

  // Reset form when user data loads
  useEffect(() => {
    if (user) {
      reset({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        phone_number: user.phone_number || '',
        new_password: '',
        locale: user.locale || 'en',
        two_factor_enabled: user.two_factor_enabled || false,
      })
    }
  }, [user, reset])

  // Locale options
  const localeOptions = locales.map((locale) => ({
    value: locale,
    label: getReadableLocaleName(locale),
  }))

  // Handle form submission
  const onSubmit = async (data: UserEditFormValues) => {
    try {
      await updateUser.mutateAsync({
        id: userId,
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        phone_number: data.phone_number || undefined,
        new_password: data.new_password || undefined,
        locale: data.locale,
        two_factor_enabled: data.two_factor_enabled,
      })
      toast.success(t('resources.users.update_success'))
      navigate(basePath)
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('resources.users.update_error')
      toast.error(message)
    }
  }

  // Handle delete
  const handleDelete = async () => {
    try {
      await deleteUser.mutateAsync(userId)
      toast.success(t('resources.users.delete_success'))
      navigate(basePath)
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('resources.users.delete_error')
      toast.error(message)
    }
  }
  
  // Handle send/resend invite
  const handleResendInvite = async () => {
    try {
      await resendInvite.mutateAsync(userId)
      toast.success(t('resources.users.send_invite_success'))
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('resources.users.send_invite_error')
      toast.error(message)
    }
  }

  if (userLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!user) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <p className="text-gray-500 dark:text-gray-400">{t('resources.users.not_found')}</p>
        <Button color="secondary" onPress={() => navigate(basePath)} className="mt-4">
          {t('resources.users.back_to_users')}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('resources.users.edit_title', { name: `${user.first_name} ${user.last_name}` })}
        subtitle={t('resources.users.edit_subtitle')}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-auto px-6 py-4">
        <div className="mx-auto max-w-2xl space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <FormInput
              control={control}
              name="first_name"
              label={t('resources.users.fields.first_name')}
              placeholder={t('resources.users.placeholders.first_name')}
              isRequired
            />

            <FormInput
              control={control}
              name="last_name"
              label={t('resources.users.fields.last_name')}
              placeholder={t('resources.users.placeholders.last_name')}
              isRequired
            />

            <FormInput
              control={control}
              name="email"
              label={t('resources.users.fields.email')}
              type="email"
              placeholder={t('resources.users.placeholders.email')}
              isRequired
            />

            <PhoneNumberInput
              control={control}
              name="phone_number"
              label={t('resources.users.fields.phone_number')}
              description={t('resources.users.descriptions.phone_number')}
            />
          </div>

          {/* Password */}
          <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
            <FormInput
              control={control}
              name="new_password"
              label={t('resources.users.fields.new_password')}
              type="password"
              placeholder={t('resources.users.placeholders.new_password')}
              description={t('resources.users.descriptions.password')}
            />
          </div>

          {/* Settings */}
          <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
            <FormSelect
              control={control}
              name="locale"
              label={t('resources.users.fields.locale')}
              options={localeOptions}
              isRequired
            />

            <div className="mt-4">
              <FormSwitch
                control={control}
                name="two_factor_enabled"
                label={t('resources.users.fields.requires_2fa')}
              />
            </div>
          </div>

          {/* Current Status (read-only) */}
          <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('resources.users.current_status')}:{' '}
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {user.is_active ? t('resources.users.active_tag') : t('resources.users.invite_tag')}
                </span>
              </p>
              
              {/* Send/Resend Invite Button - show for non-active users */}
              {canSendInvite && (
                <Button
                  color="secondary"
                  type="button"
                  size="sm"
                  onPress={handleResendInvite}
                  isDisabled={resendInvite.isPending}
                  iconLeading={Send01Icon}
                >
                  {resendInvite.isPending
                    ? t('resources.users.sending_invite')
                    : isPendingStatus
                      ? t('resources.users.resend_invite')
                      : t('resources.users.send_invite')}
                </Button>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between border-t border-gray-200 pt-6 dark:border-gray-700">
            <Button
              color="primary-destructive"
              type="button"
              onPress={() => setShowDeleteDialog(true)}
              iconLeading={Trash01Icon}
            >
              {t('resources.users.delete_title')}
            </Button>

            <div className="flex items-center gap-3">
              <Button color="secondary" type="button" onPress={() => navigate(basePath)}>
                {t('resources.generic.cancel')}
              </Button>
              <Button
                color="primary"
                type="submit"
                isDisabled={isSubmitting || !isDirty}
                iconLeading={CheckIcon}
              >
                {isSubmitting ? t('resources.generic.loading') : t('resources.generic.save')}
              </Button>
            </div>
          </div>
        </div>
      </form>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title={t('resources.users.delete_title')}
        description={t('resources.users.delete_description', { name: `${user.first_name} ${user.last_name}` })}
        confirmLabel={t('resources.generic.delete')}
        variant="error"
        isLoading={deleteUser.isPending}
      />
    </div>
  )
}

export default UserEditPage
